import { ArrowLeft, Moon, Play, Square, Sun } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CompanionBody, type CompanionGesture } from '@/components/observer/CompanionBody';
import { STATE_COLOR_MS, STATE_SIZE } from '@/components/observer/companionTheme';
import type { ObserverMotion, ObserverPresence, ObserverState } from '@/components/observer/observerContext';
import { Button, IconButton, Switch } from '@/components/ui';
import { useTheme } from '@/theme/ThemeProvider';

/*
  /design/observer/lab — стенд компаньона. Он стоит на одном месте в четырёхкратном увеличении,
  рядом — он же в настоящем размере на границе блока. Каждое состояние и каждый жест
  запускаются своей кнопкой, есть автопрогон. Здесь движения обкатываются глазами,
  и только потом переезжают на площадку и в оболочку (docs/observer-design.md §4.6).
*/

const states: { value: ObserverState; label: string; note: string }[] = [
  { value: 'idle', label: 'покой', note: 'круг, дышит; аура ровная' },
  { value: 'thinking', label: 'думает', note: 'дышит чаще; аура пульсирует; крупнее' },
  { value: 'writing', label: 'пишет', note: 'кивает в такт' },
  { value: 'download', label: 'качает', note: 'капля вверх; аура течёт вниз' },
  { value: 'upload', label: 'отдаёт', note: 'капля вверх; аура течёт вверх' },
  { value: 'update', label: 'долгая операция', note: 'самый крупный; тяжёлое дыхание' },
  { value: 'success', label: 'готово', note: 'вспышка ауры; цвет быстро, назад медленно' },
  { value: 'error', label: 'ошибка', note: 'цвет скачком; аура тускнеет' },
];

const gestures: { value: CompanionGesture; label: string; ms: number; note: string }[] = [
  { value: 'bounce', label: 'прыжок', ms: 760, note: 'присел, взлетел, два отскока' },
  { value: 'shiver', label: 'дрожь «брр»', ms: 640, note: 'ошибка, отказ в невозможном' },
  { value: 'nod', label: 'кивок', ms: 520, note: 'подтверждение, сообщение' },
  { value: 'squash', label: 'лепёшка', ms: 420, note: 'момент посадки' },
  { value: 'swell', label: 'раздувание', ms: 640, note: 'успех' },
  { value: 'blink', label: 'сжатие', ms: 360, note: 'перед телепортом' },
];

const motions: { value: ObserverMotion; label: string }[] = [
  { value: 'restrained', label: 'сдержанно' },
  { value: 'pronounced', label: 'выражено' },
  { value: 'cartoon', label: 'мультяшно' },
];

const presences: { value: ObserverPresence; label: string }[] = [
  { value: 'quiet', label: 'тихий' },
  { value: 'companion', label: 'компаньон' },
];

const SCALE = 4;

export function CompanionLab() {
  const { mode, toggle } = useTheme();
  const [state, setState] = useState<ObserverState>('idle');
  const [gesture, setGesture] = useState<CompanionGesture | null>(null);
  const [motion, setMotion] = useState<ObserverMotion>('pronounced');
  const [presence, setPresence] = useState<ObserverPresence>('companion');
  const [followCursor, setFollowCursor] = useState(true);
  const [look, setLook] = useState({ x: 0, y: 0 });
  const [running, setRunning] = useState(false);
  const bigRef = useRef<HTMLDivElement>(null);
  const gestureTimer = useRef(0);

  const play = useCallback((g: CompanionGesture) => {
    const ms = gestures.find((x) => x.value === g)?.ms ?? 600;
    window.clearTimeout(gestureTimer.current);
    // Сброс перед повтором, чтобы тот же жест запускался заново.
    setGesture(null);
    window.setTimeout(() => setGesture(g), 20);
    gestureTimer.current = window.setTimeout(() => setGesture(null), ms + 40);
  }, []);

  // Взгляд: смещение к курсору относительно центра большого компаньона.
  useEffect(() => {
    if (!followCursor) return;
    const onMove = (e: PointerEvent) => {
      const el = bigRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      setLook({
        x: Math.max(-1, Math.min(1, (e.clientX - cx) / 360)),
        y: Math.max(-1, Math.min(1, (e.clientY - cy) / 360)),
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [followCursor]);

  // Автопрогон: состояния по кругу, между ними жесты.
  useEffect(() => {
    if (!running) return;
    const script: Array<{ state?: ObserverState; gesture?: CompanionGesture }> = [
      { state: 'idle' },
      { gesture: 'bounce' },
      { state: 'thinking' },
      { state: 'writing' },
      { gesture: 'nod' },
      { state: 'download' },
      { state: 'upload' },
      { state: 'update' },
      { state: 'success' },
      { gesture: 'swell' },
      { state: 'error' },
      { gesture: 'shiver' },
      { state: 'idle' },
      { gesture: 'squash' },
      { gesture: 'blink' },
    ];
    let i = 0;
    const id = window.setInterval(() => {
      const step = script[i % script.length];
      if (step.state) setState(step.state);
      if (step.gesture) play(step.gesture);
      i += 1;
    }, 1700);
    return () => window.clearInterval(id);
  }, [running, play]);

  const size = STATE_SIZE[state];

  return (
    <div className="h-screen overflow-y-auto bg-frame text-fg">
      <header className="sticky top-0 z-raised flex items-center gap-4 border-b border-line bg-canvas px-6 py-3">
        <Link
          to="/design/observer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="size-3.5" />
          Площадка
        </Link>
        <span className="text-sm font-semibold text-fg">Стенд компаньона</span>
        <Link to="/design/observer/liquid" className="text-xs font-medium text-brand-fg hover:underline">
          Жидкое тело
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant={running ? 'primary' : 'secondary'} onClick={() => setRunning((v) => !v)}>
            {running ? <Square className="size-3.5" /> : <Play className="size-3.5" />}
            {running ? 'Остановить прогон' : 'Прогнать всё'}
          </Button>
          <IconButton
            aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            variant="secondary"
            size="sm"
            onClick={toggle}
          >
            {mode === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-6 py-8 lg:grid-cols-[1fr_360px]">
        {/* Сцена */}
        <section className="rounded-lg border border-line bg-card p-6">
          <div className="grid gap-6 md:grid-cols-[1fr_1fr]">
            {/* Крупно */}
            <div className="flex flex-col items-center gap-4 rounded-md border border-line bg-canvas p-6">
              <div className="text-2xs font-semibold text-fg-subtle uppercase">В четыре раза крупнее</div>
              <div ref={bigRef} className="grid h-64 w-full place-items-center">
                <CompanionBody
                  state={state}
                  gesture={gesture}
                  size={size * SCALE}
                  look={look}
                  motion={motion}
                  presence={presence}
                />
              </div>
              <div className="flex items-baseline gap-4 text-xs text-fg-subtle tabular-nums">
                <span>размер {size} px</span>
                <span>цвет за {STATE_COLOR_MS[state]} мс</span>
              </div>
            </div>

            {/* Настоящий размер на границе блока */}
            <div className="flex flex-col gap-4">
              <div className="text-2xs font-semibold text-fg-subtle uppercase">Настоящий размер</div>
              <div className="relative mt-4 rounded-md border border-line bg-canvas p-5">
                <div className="absolute top-0 right-8 -translate-y-1/2">
                  <CompanionBody
                    state={state}
                    gesture={gesture}
                    look={look}
                    motion={motion}
                    presence={presence}
                  />
                </div>
                <div className="text-sm font-semibold text-fg">Карточка</div>
                <p className="mt-1 text-xs text-fg-muted">
                  Так он сидит на верхней границе блока: центр на линии, отступ справа. Здесь видно, что
                  переживает уменьшение, а что нет.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  {['Строка списка', 'Ещё строка', 'И ещё одна'].map((t) => (
                    <div key={t} className="rounded-sm border border-line px-3 py-2 text-xs text-fg-muted">
                      {t}
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-xs text-fg-subtle">
                {states.find((s) => s.value === state)?.note}
                {gesture ? ` · жест: ${gestures.find((g) => g.value === gesture)?.label}` : ''}
              </p>
            </div>
          </div>
        </section>

        {/* Пульт */}
        <aside className="flex flex-col gap-5">
          <section className="rounded-lg border border-line bg-card p-5">
            <h2 className="type-subheading text-fg">Состояние</h2>
            <p className="mt-1 text-xs text-fg-muted">
              Каждое задаёт размер, силуэт, ауру и цвет. Держится, пока не сменится.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {states.map((s) => (
                <Button
                  key={s.value}
                  size="sm"
                  variant={state === s.value ? 'primary' : 'secondary'}
                  onClick={() => setState(s.value)}
                  title={s.note}
                >
                  {s.label}
                </Button>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-line bg-card p-5">
            <h2 className="type-subheading text-fg">Жест</h2>
            <p className="mt-1 text-xs text-fg-muted">
              Короткий гость поверх состояния, меньше секунды, один за раз.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {gestures.map((g) => (
                <Button
                  key={g.value}
                  size="sm"
                  variant="secondary"
                  onClick={() => play(g.value)}
                  title={g.note}
                >
                  {g.label}
                </Button>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-line bg-card p-5">
            <h2 className="type-subheading text-fg">Сила движения</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {motions.map((m) => (
                <Button
                  key={m.value}
                  size="sm"
                  variant={motion === m.value ? 'primary' : 'secondary'}
                  onClick={() => setMotion(m.value)}
                >
                  {m.label}
                </Button>
              ))}
            </div>
            <h2 className="mt-5 type-subheading text-fg">Характер</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {presences.map((p) => (
                <Button
                  key={p.value}
                  size="sm"
                  variant={presence === p.value ? 'primary' : 'secondary'}
                  onClick={() => setPresence(p.value)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="mt-5">
              <Switch
                size="sm"
                checked={followCursor}
                onCheckedChange={(v) => {
                  setFollowCursor(v);
                  if (!v) setLook({ x: 0, y: 0 });
                }}
                label="Блик следит за курсором"
              />
            </div>
          </section>
        </aside>
      </main>
    </div>
  );
}
